import { createSlice } from '@reduxjs/toolkit';

// Lib
import { checkAllCheckbox, Compound, filterByAccessibility, updatedDataByKey, priceAvailable } from 'lib/prices';

// Type
import { Supplier } from 'pages/MoleculeProduct/reducer';

export type TPrice = {
	mg?: number,
	priceEur?: number,
	priceUsd?: number,
	available?: boolean,
	value?: string;
	type?: string;
	label?: string;
	price?: string;
}
export type TContent = {
	id: string,
	qed: number,
	cas: string,
	name: string,
	wqed: number,
	dbId: string,
	currentTypePrice: string;
	prices: {
		supplier?: string;
		prices?: TPrice[];
	}[],
	supIds?: Supplier[];
	onStock: boolean,
	cxSmiles: string,
	iupacName: string,
	currentPrice?: any,
	selected?: boolean,
	isPOA: boolean;
	description: string,
}
type TData = {
	size?: number,
	number?: number,
	totalPages?: number,
	content?: Compound[],
	totalElements?: number,
}

// Interface
interface IEnv {
	data: TData;
	dataOld: TData;
	value: string;
	message: string,
	loading: boolean;
	dataSearch: TData;
	checkboxAll?: boolean;
	loadingSearch?: boolean,
	isFilterByStock?: boolean,
	accessibility?: { [index: string]: string },

	numberOfCompounds?: number;

	toggleFilter: boolean,

	progress: {
		total: number;
		current: number;
	},

	name: string;
	compoundsCount: number;
}

export const initialState: IEnv = {
	data: {
		size: 20,
		number: 0,
		content: [],
		totalPages: 0,
		totalElements: 0
	},
	dataOld: {
		size: 20,
		number: 0,
		content: [],
		totalPages: 0,
		totalElements: 0
	},
	dataSearch: {
		size: 20,
		number: 0,
		content: [],
		totalPages: 0,
		totalElements: 0
	},
	numberOfCompounds: 20,
	value: '',
	message: '',
	loading: false,
	toggleFilter: false,
	loadingSearch: false,
	isFilterByStock: false,
	checkboxAll: false,
	accessibility: { label: 'All', value: 'ALL' },

	progress: {
		current: 0,
		total: 0
	},

	name: '',
	compoundsCount: 0
};

export const librariesCompoundsSlice = createSlice({
	name: 'librariesCompounds',
	initialState,
	reducers: {
		updateTitle: (state, action) => {
			state.name = action.payload.name;
			state.compoundsCount = action.payload.compoundsCount;
		},

		handleResetSearch: (state) => {
			state.checkboxAll = false;
			state.data = state.dataOld;
		},
		getLibrariesCompoundsPartial: (state, action) => {
			state.loading = false;
			state.data = action.payload.data;
			state.dataOld = action.payload.dataOld;
			state.checkboxAll = action.payload.checkboxAll;

			state.progress = {
				current: action.payload.chunk,
				total: action.payload.totalChunks
			};
		},

		changeToggleFilter: (state) => {
			state.toggleFilter = !state.toggleFilter;
		},
		handlerChangeCurrentTypePrice: (state, action) => {
			const id = action.payload.id;
			const type = action.payload.type;

			state.data.content = updatedDataByKey({
				id: id,
				value: type,
				key: 'currentTypePrice',
				data: state.data.content
			});
		},

		handlerClearState: (state) => {
			return {
				...state,
				...initialState
			};
		},
		handlerChangeAccessibility: (state, action) => {
			const value = action.payload.value;
			const dataOld = state?.dataOld?.content || [];

			state.data.number = 0;
			state.checkboxAll = false;
			state.accessibility = action.payload;

			const configData = {
				'ALL': filterByAccessibility(dataOld as any, 'ALL'),
				'ON_STOCK': filterByAccessibility(dataOld as any, 'ON_STOCK'),
				'ON_STOCK_POA': filterByAccessibility(dataOld as any, 'ON_STOCK_POA'),
				'OUT_OF_STOCK': filterByAccessibility(dataOld as any, 'OUT_OF_STOCK')
			};

			// @ts-ignore
			const data = configData[value] || [];

			state.data.content = data?.length
				? data?.map((item: Compound) => {
					return {
						...item,
						selected: false
					};
				}) : [];
		},

		getLibrariesCompounds: (state, action) => {
			state.loading = true;
		},
		getLibrariesCompoundsError: (state) => {
			state.loading = false;
		},
		getLibrariesCompoundsSuccess: (state) => {
			state.loading = false;
		},

		handlerChangePage: (state, action) => {
			state.data.number = action.payload;
		},


		handleChangePricesContent: (state, action) => {
			const prepackage = action.payload;

			state.data.content = state.data.content?.map((item: Compound) => {
				const updatedItem = { ...item, currentPrice: prepackage };
				const canSelect = !updatedItem.isPOA &&
					updatedItem.onStock &&
					priceAvailable(updatedItem)?.available;

				return {
					...updatedItem,
					selected: state.checkboxAll && canSelect
				};
			});
		},
		handlerChangeCheckboxSelectAll: (state) => {
			state.checkboxAll = !state.checkboxAll;
			const content = state?.data?.content || [];

			state.data.content = content?.length
				? content?.map((item: Compound) => {
					const canSelect =
						!item.isPOA &&
						item.onStock &&
						priceAvailable(item)?.available;

					return {
						...item,
						selected: state.checkboxAll && canSelect
					};
				}) : [];
		},
		handlerChangeCheckboxSelect: (state, action) => {
			const id = action.payload.id;
			const content = state?.data?.content;

			const result = updatedDataByKey({
				id: id,
				value: '',
				key: 'selected',
				data: content
			});

			state.data.content = result;
			state.checkboxAll = checkAllCheckbox(result);
		},

		changeSearchInput: (state, action) => {
			state.value = action.payload;
		},
		searchLibrariesCompounds: (state, action) => {
			state.loadingSearch = true;
		},
		searchLibrariesCompoundsError: (state, action) => {
			state.loading = false;
			state.message = action.payload;
		},
		searchLibrariesCompoundsSuccess: (state, action) => {
			state.loading = false;
			state.checkboxAll = false;
			state.dataSearch = action.payload.dataSearch;
		},

		handlerChangeSize: (state, action) => {
			state.numberOfCompounds = action.payload;
		},

		handlerChangeQuantity: (state, action) => {
			const id = action.payload.id;
			const quantity = action.payload.quantity;

			state.data.content = updatedDataByKey({
				id: id,
				value: quantity,
				key: 'quantity',
				data: state?.data?.content as Compound[]
			});

		},
		handlerChangePrice: (state, action) => {
			const id = action.payload.id;
			const currentPrice = action.payload.currentPrice;

			state.data.content = updatedDataByKey({
				id: id,
				value: currentPrice,
				key: 'currentPrice',
				data: state?.data?.content as Compound[]
			});
		},

		handlerUpdateContent: (state, action) => {
			state.data.content = action.payload;
		},

		handlerClearAllCheckboxSelected: (state) => {
			state.checkboxAll = false;
			state.data.content = state?.data?.content?.map((item: Compound) => ({
				...item,
				selected: false
			}));
		},

		addAllLibrariesToProject: (state, action) => {
		},
		addAllLibrariesToProjectError: () => {
		},
		addAllLibrariesToProjectSuccess: () => {
		}
	}
});

export const {
	updateTitle,

	changeSearchInput,
	handleResetSearch,
	getLibrariesCompoundsPartial,

	handleChangePricesContent,

	changeToggleFilter,
	handlerChangeCurrentTypePrice,

	handlerClearState,
	handlerChangePage,
	handlerChangeSize,
	handlerChangePrice,
	handlerUpdateContent,
	getLibrariesCompounds,
	handlerChangeQuantity,
	addAllLibrariesToProject,
	searchLibrariesCompounds,
	getLibrariesCompoundsError,
	handlerChangeAccessibility,
	handlerChangeCheckboxSelect,
	getLibrariesCompoundsSuccess,
	addAllLibrariesToProjectError,
	searchLibrariesCompoundsError,
	handlerChangeCheckboxSelectAll,
	addAllLibrariesToProjectSuccess,
	handlerClearAllCheckboxSelected,
	searchLibrariesCompoundsSuccess
} = librariesCompoundsSlice.actions;

export default librariesCompoundsSlice.reducer;
